import { useCallback, useEffect, useState } from "react";
import Card, { CardAction, Empty } from "./Card";
import Modal from "../common/Modal";
import Button from "../common/Button";
import { getProfit, saveCostSettings, saveJobCosts } from "../../api/insights";
import { formatMoney } from "../../utils/money";
import { formatEasternDate } from "../../utils/dates";

// What each event actually earns. A cost model (staff, mileage, fixed costs,
// card fees) estimates every booked job; entering a job's real miles/hours/
// extras replaces the estimate for that job.

const usd0 = (cents) => `${cents < 0 ? "-" : ""}$${Math.abs(Math.round(Number(cents || 0) / 100)).toLocaleString()}`;
const pct = (m) => (m == null ? "—" : `${Math.round(m * 100)}%`);
const LOW_MARGIN = 0.3;
const INITIAL_ROWS = 6;

const input =
  "w-full px-3 py-2.5 text-sm border border-birdie-green/25 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-birdie-green/40";

export default function ProfitSection() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [editingModel, setEditingModel] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    getProfit()
      .then(setData)
      .catch((e) => setError(e.message || "Could not load profit."));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <Card title="Profit per event">
        <Empty>{error}</Empty>
      </Card>
    );
  }
  if (!data) return null;
  if (data.needs_migration) {
    return (
      <Card title="Profit per event">
        <Empty>Waiting on a database update. It applies automatically on the next backend deploy.</Empty>
      </Card>
    );
  }

  const modal = editingModel && (
    <CostModelModal
      model={data.model}
      onClose={() => setEditingModel(false)}
      onSaved={() => {
        setEditingModel(false);
        load();
      }}
    />
  );

  if (!data.configured) {
    return (
      <Card title="Profit per event" subtitle="What each job really earns after costs">
        <p className="text-sm text-birdie-charcoal/70">
          Tell it what an event costs you — staff, mileage, fixed costs — and every booked job gets a
          profit estimate. Takes a minute; you can fine-tune any job later.
        </p>
        <Button onClick={() => setEditingModel(true)} className="mt-3">
          Set up my costs
        </Button>
        {modal}
      </Card>
    );
  }

  const past = data.jobs.filter((j) => j.past);
  const upcoming = data.jobs.filter((j) => !j.past);
  const ordered = [...upcoming.slice().reverse(), ...past]; // soonest upcoming first, then most recent
  const rows = showAll ? ordered : ordered.slice(0, INITIAL_ROWS);

  return (
    <Card
      title="Profit per event"
      subtitle="After staff, travel, card fees and fixed costs · sales tax excluded"
      action={<CardAction onClick={() => setEditingModel(true)}>Costs</CardAction>}
    >
      <div className="grid grid-cols-2 gap-2.5">
        <Summary label="Last 90 days" s={data.last_90} />
        <Summary label="Next 90 days (projected)" s={data.next_90} />
      </div>

      {ordered.length === 0 ? (
        <div className="mt-3">
          <Empty>No booked events in the last 180 days or next 90.</Empty>
        </div>
      ) : (
        <>
          <ul className="mt-3 divide-y divide-birdie-green/10">
            {rows.map((j) => (
              <li key={j.id}>
                <button
                  onClick={() => setEditingJob(j)}
                  className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-birdie-cream/60 -mx-1 px-1 rounded-lg"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-birdie-charcoal truncate">{j.customer_name}</div>
                    <div className="text-[11px] text-birdie-charcoal/50">
                      {formatEasternDate(j.event_date)} · {usd0(j.revenue_cents)}
                      {!j.past && " · upcoming"}
                      {!j.has_actuals && " · est."}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold tabular-nums text-birdie-charcoal">
                      {usd0(j.profit_cents)}
                    </div>
                    <div
                      className={`text-[11px] tabular-nums ${
                        j.margin != null && j.margin < LOW_MARGIN ? "text-amber-700 font-semibold" : "text-birdie-charcoal/50"
                      }`}
                    >
                      {pct(j.margin)} margin{j.margin != null && j.margin < LOW_MARGIN ? " · low" : ""}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {ordered.length > INITIAL_ROWS && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-2 text-xs font-semibold text-birdie-green hover:underline"
            >
              {showAll ? "Show fewer" : `Show all ${ordered.length}`}
            </button>
          )}
          <p className="mt-2 text-[11px] text-birdie-charcoal/45">
            Tap a job to enter its real miles, hours or extras. “est.” uses your cost settings.
          </p>
        </>
      )}

      {modal}
      {editingJob && (
        <JobCostModal
          job={editingJob}
          onClose={() => setEditingJob(null)}
          onSaved={() => {
            setEditingJob(null);
            load();
          }}
        />
      )}
    </Card>
  );
}

function Summary({ label, s }) {
  return (
    <div className="rounded-xl bg-birdie-cream/70 px-3 py-2.5">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className="text-lg font-semibold text-birdie-charcoal tabular-nums">{usd0(s.profit_cents)}</div>
      <div className="text-[10px] text-birdie-charcoal/50 leading-snug">
        {s.jobs} event{s.jobs === 1 ? "" : "s"} · {pct(s.margin)} margin
        {s.jobs > 0 && (
          <>
            <br />
            {usd0(s.avg_profit_cents)}/event
            {s.profit_per_hour_cents != null && ` · ${usd0(s.profit_per_hour_cents)}/hr`}
          </>
        )}
      </div>
    </div>
  );
}

// ---- cost model ---------------------------------------------------------

const MODEL_FIELDS = [
  { key: "staff_count", label: "Staff at each event", unit: "people", step: 1 },
  { key: "staff_hourly_cents", label: "Pay per staff hour", unit: "$", money: true },
  { key: "extra_hours", label: "Extra hours per event", hint: "Load-in, setup, driving, pack-down", unit: "hrs", step: 0.5 },
  { key: "cost_per_mile_cents", label: "Cost per mile", hint: "Fuel + wear on the bus", unit: "$", money: true },
  { key: "default_miles", label: "Typical round trip", hint: "Used when a job has no miles entered", unit: "mi", step: 1 },
  { key: "fixed_cents", label: "Fixed cost per event", hint: "Insurance, consumables, cleaning…", unit: "$", money: true },
  { key: "card_fee_pct", label: "Card fee", unit: "%", step: 0.1 },
  { key: "card_fee_fixed_cents", label: "Card fee per payment", unit: "$", money: true },
];

const toField = (f, v) => (v == null ? "" : f.money ? String(Number(v) / 100) : String(v));
const fromField = (f, s) => (f.money ? Math.round(Number(s) * 100) : Number(s));

function CostModelModal({ model, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(MODEL_FIELDS.map((f) => [f.key, toField(f, model[f.key])]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setSaving(true);
    setError("");
    try {
      const body = {};
      for (const f of MODEL_FIELDS) {
        const s = String(form[f.key]).trim();
        if (s === "" || Number.isNaN(Number(s))) throw new Error(`${f.label}: enter a number (0 is fine).`);
        body[f.key] = fromField(f, s);
      }
      await saveCostSettings(body);
      onSaved();
    } catch (e) {
      setError(e.message || "Could not save.");
      setSaving(false);
    }
  }

  return (
    <Modal isOpen title="What an event costs you" onClose={onClose} maxWidthClass="max-w-md">
      <div className="space-y-3">
        <p className="text-xs text-birdie-charcoal/60">
          Rough is fine — these give every job an estimate. Sales tax is never counted as income.
        </p>
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {MODEL_FIELDS.map((f) => (
          <NumberField
            key={f.key}
            field={f}
            value={form[f.key]}
            onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
          />
        ))}
        <Button onClick={save} disabled={saving} className="w-full py-3">
          {saving ? "Saving…" : "Save costs"}
        </Button>
      </div>
    </Modal>
  );
}

// ---- one job ------------------------------------------------------------

const JOB_FIELDS = [
  { key: "miles", label: "Round-trip miles", unit: "mi", step: 1 },
  { key: "staff_count", label: "Staff", unit: "people", step: 1 },
  { key: "staff_hours", label: "Hours each (incl. setup + driving)", unit: "hrs", step: 0.5 },
  { key: "other_cents", label: "Other costs", hint: "Tolls, parking, extra gear…", unit: "$", money: true },
];

function JobCostModal({ job, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...Object.fromEntries(JOB_FIELDS.map((f) => [f.key, toField(f, job.actuals[f.key])])),
    note: job.actuals.note || "",
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // What the estimate is using right now, shown as placeholders.
  const estimate = {
    miles: job.miles,
    staff_count: job.staff_count,
    staff_hours: job.staff_hours,
    other_cents: 0,
  };

  async function save() {
    setSaving(true);
    setError("");
    try {
      const body = { note: form.note.trim() || null };
      for (const f of JOB_FIELDS) {
        const s = String(form[f.key]).trim();
        if (s !== "" && Number.isNaN(Number(s))) throw new Error(`${f.label}: enter a number.`);
        body[f.key] = s === "" ? null : fromField(f, s);
      }
      await saveJobCosts(job.id, body);
      onSaved();
    } catch (e) {
      setError(e.message || "Could not save.");
      setSaving(false);
    }
  }

  const c = job.costs;
  return (
    <Modal isOpen title={job.customer_name} onClose={onClose} maxWidthClass="max-w-md">
      <div className="space-y-3">
        <div className="text-xs text-birdie-charcoal/55">
          {job.quote_number} · {formatEasternDate(job.event_date)} · {job.title || "Event"}
        </div>

        <table className="w-full text-sm">
          <tbody>
            <Line label="Revenue (excl. tax)" cents={job.revenue_cents} strong />
            <Line label={`Staff · ${job.staff_count} × ${job.staff_hours}h`} cents={-c.labor_cents} />
            <Line label={`Travel · ${job.miles} mi`} cents={-c.travel_cents} />
            <Line label="Card fees" cents={-c.card_fees_cents} />
            <Line label="Fixed costs" cents={-c.fixed_cents} />
            {c.other_cents > 0 && <Line label="Other" cents={-c.other_cents} />}
            <tr className="border-t border-birdie-green/15">
              <td className="pt-2 font-semibold text-birdie-charcoal">Profit</td>
              <td className="pt-2 text-right font-semibold tabular-nums text-birdie-charcoal">
                {formatMoney(job.profit_cents)} <span className="text-birdie-charcoal/50 font-normal">({pct(job.margin)})</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div className="pt-1 text-xs font-semibold text-birdie-charcoal/70">
          Actuals for this job <span className="font-normal text-birdie-charcoal/45">— leave blank to use the estimate</span>
        </div>
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        {JOB_FIELDS.map((f) => (
          <NumberField
            key={f.key}
            field={f}
            value={form[f.key]}
            placeholder={toField(f, estimate[f.key])}
            onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))}
          />
        ))}
        <input
          className={input}
          placeholder="Note (optional)"
          value={form.note}
          onChange={(e) => setForm((s) => ({ ...s, note: e.target.value }))}
          maxLength={500}
        />
        <Button onClick={save} disabled={saving} className="w-full py-3">
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </Modal>
  );
}

function Line({ label, cents, strong }) {
  return (
    <tr>
      <td className={`py-0.5 ${strong ? "text-birdie-charcoal font-medium" : "text-birdie-charcoal/65"}`}>{label}</td>
      <td className={`py-0.5 text-right tabular-nums ${strong ? "text-birdie-charcoal font-medium" : "text-birdie-charcoal/65"}`}>
        {formatMoney(cents)}
      </td>
    </tr>
  );
}

function NumberField({ field, value, onChange, placeholder }) {
  const id = `f-${field.key}`;
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-birdie-charcoal/70 mb-1">
        {field.label}
        {field.hint && <span className="font-normal text-birdie-charcoal/45"> · {field.hint}</span>}
      </label>
      <div className="relative">
        {field.unit === "$" && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-birdie-charcoal/45">$</span>
        )}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min="0"
          step={field.money ? "0.01" : field.step || "any"}
          className={`${input} ${field.unit === "$" ? "pl-7" : ""} ${field.unit !== "$" ? "pr-14" : ""}`}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {field.unit !== "$" && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-birdie-charcoal/45">{field.unit}</span>
        )}
      </div>
    </div>
  );
}
